import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  CircularProgress,
  Container,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import { useSubscriptionStatus } from "../../contexts/SubscriptionStatusContext";
import * as subscriptionSerivces from "../../contexts/services/userSubscriptionServices";

import PlansTab from "../../components/Settings/Plans";
import TenantBillingOverview from "../../components/Settings/Billing/TenantBillingOverview";
import BaseDialog from "../../components/utils/dialog/BaseDialog";
import { useErrorDialog } from "../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../contexts/SuccessDialogContext";
import ComponentLoading from "../../components/App/ComponentLoading";

function unwrapData(res) {
  if (
    res &&
    typeof res === "object" &&
    !Array.isArray(res) &&
    (res.status === "success" || res.status === "error") &&
    "data" in res
  ) {
    return res.data;
  }
  return res;
}

const PAYMENT_SUCCESS_REDIRECT_MS = 3000;
const PAYMENT_SUCCESS_DIALOG_Z_INDEX = 10000;

const BillingPanel = () => {
  const { global_authState } = useContext(AuthContext);
  const navigate = useNavigate();
  const { clearSubscriptionStatus, refreshSubscriptionStatus } =
    useSubscriptionStatus() || {};

  const [showPlansTab, setShowPlansTab] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(null);
  const [redirectCountdown, setRedirectCountdown] = useState(3);
  const paymentRedirectTimerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [terminating, setTerminating] = useState(false);
  const [charging, setCharging] = useState(false);
  const [planActionLoading, setPlanActionLoading] = useState(false);
  const [catalogPlans, setCatalogPlans] = useState([]);
  const [catalogPlansLoading, setCatalogPlansLoading] = useState(false);

  const [billingState, setBillingState] = useState({
    billingData: {},
    subscriptionStat: {},
  });

  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentHistoryLoaded, setPaymentHistoryLoaded] = useState(false);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);

  const handleError = useErrorDialog();
  const handleSuccess = useSuccessDialog();

  const subscriptionId = global_authState?.subscription?.id;

  const fetchBillingState = useCallback(async () => {
    if (!subscriptionId) return;
    const [billingRes, subscriptionStatRes] = await Promise.all([
      subscriptionSerivces.getNextBillingPayment(),
      subscriptionSerivces.getSubscriptionStatus(),
    ]);
    const billingData = unwrapData(billingRes);
    const subscriptionStat = unwrapData(subscriptionStatRes);
    setBillingState({ billingData, subscriptionStat });
  }, [subscriptionId]);

  const syncHitpayPaymentStatus = useCallback(async () => {
    if (!subscriptionId) return;
    try {
      await subscriptionSerivces.syncPaymentStatus();
    } catch (error) {
      // Non-blocking: billing page can still load current server state.
      console.error("syncPaymentStatus failed:", error);
    }
  }, [subscriptionId]);

  const handleLoadPaymentHistory = useCallback(async () => {
    if (!subscriptionId) return;
    setPaymentHistoryLoading(true);
    try {
      const res = await subscriptionSerivces.getSubscriptionPayments();
      const data = unwrapData(res);
      setPaymentHistory(Array.isArray(data) ? data : []);
      setPaymentHistoryLoaded(true);
    } catch (error) {
      console.error("getSubscriptionPayments failed:", error);
      handleError(error?.message || "Failed to load payment history");
    } finally {
      setPaymentHistoryLoading(false);
    }
  }, [subscriptionId, handleError]);

  useEffect(() => {
    if (!subscriptionId) return;

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        await syncHitpayPaymentStatus();
        await fetchBillingState();
      } catch (error) {
        console.error("Failed to fetch billing data:", error);
        if (!cancelled) {
          handleError(error?.message || "Unexpected Error Occurs");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [subscriptionId, fetchBillingState, handleError, syncHitpayPaymentStatus]);

  const loadCatalogPlans = useCallback(async () => {
    setCatalogPlansLoading(true);
    try {
      const res = await subscriptionSerivces.getPlans();
      const raw = unwrapData(res);
      const list = Array.isArray(raw) ? raw : (raw?.data ?? raw?.plans ?? []);
      setCatalogPlans(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("getPlans failed:", error);
      handleError(error?.message || "Could not load plans");
    } finally {
      setCatalogPlansLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    if (showPlansTab) loadCatalogPlans();
  }, [showPlansTab, loadCatalogPlans]);

  useEffect(() => {
    if (!paymentSuccess) return undefined;

    setRedirectCountdown(3);
    const countdownInterval = setInterval(() => {
      setRedirectCountdown((prev) => Math.max(0, prev - 1));
    }, 1000);

    paymentRedirectTimerRef.current = setTimeout(async () => {
      clearSubscriptionStatus?.();
      await refreshSubscriptionStatus?.();
      navigate("/consolePanel", { replace: true });
    }, PAYMENT_SUCCESS_REDIRECT_MS);

    return () => {
      clearInterval(countdownInterval);
      if (paymentRedirectTimerRef.current) {
        clearTimeout(paymentRedirectTimerRef.current);
        paymentRedirectTimerRef.current = null;
      }
    };
  }, [
    paymentSuccess,
    navigate,
    clearSubscriptionStatus,
    refreshSubscriptionStatus,
  ]);

  /** Matches server rule: only `user_auth.status === "guest"` may start free trial. */
  const isGuestForTrial = global_authState?.status === "guest";

  const handleChangePlan = async (planId) => {
    if (!subscriptionId || !planId) {
      handleError("Missing subscription or plan. Please try again.");
      return;
    }
    try {
      setPlanActionLoading(true);
      const res = await subscriptionSerivces.changePlan({
        subscription_id: subscriptionId,
        plan_id: planId,
      });
      const data = unwrapData(res);
      await fetchBillingState();
      handleSuccess(
        typeof data?.message === "string" ? data.message : undefined,
        false,
        "Plan change scheduled"
      );
      setShowPlansTab(false);
    } catch (error) {
      console.error("changePlan failed:", error);
      handleError(error?.message || "Unexpected Error Occurs");
    } finally {
      setPlanActionLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!subscriptionId) {
      handleError("Missing subscription. Please try again.");
      return;
    }

    try {
      setCharging(true);
      const res = await subscriptionSerivces.chargeSavedCard({
        subscription_id: subscriptionId,
      });
      const result = unwrapData(res);
      await fetchBillingState();
      clearSubscriptionStatus?.();
      const message =
        typeof result?.message === "string"
          ? result.message
          : "Payment completed successfully";
      setPaymentSuccess({ message });
    } catch (error) {
      console.error("Payment failed:", error);
      handleError(error?.message || "Unexpected Error Occurs");
    } finally {
      setCharging(false);
    }
  };

  const handleActivate = async () => {
    try {
      setActivating(true);
      const res = await subscriptionSerivces.activateSubscription({
        subscription_id: subscriptionId,
      });
      const data = unwrapData(res);
      const redirectUrl = data?.redirect_url;
      if (redirectUrl) {
        window.location.replace(redirectUrl);
      }
    } catch (error) {
      console.error("Failed to activate subscription:", error);
      handleError(error?.message || "Unexpected Error Occurs");
    } finally {
      setActivating(false);
    }
  };

  const handleTerminate = async () => {
    try {
      setTerminating(true);
      const res = await subscriptionSerivces.terminateSubscription({
        subscription_id: subscriptionId,
      });
      const data = unwrapData(res);
      await fetchBillingState();
      handleSuccess(
        typeof data?.message === "string" ? data.message : undefined,
        false,
        "Subscription will end after the current billing period"
      );
    } catch (error) {
      console.error("Failed to terminate subscription:", error);
      handleError(error?.message || "Unexpected Error Occurs");
    } finally {
      setTerminating(false);
    }
  };

  const revertPendingPlanRequest = async () => {
    if (!subscriptionId) return;
    try {
      setPlanActionLoading(true);
      const res = await subscriptionSerivces.revertPendingPlan({
        subscription_id: subscriptionId,
      });
      const data = unwrapData(res);
      await fetchBillingState();
      handleSuccess(
        typeof data?.message === "string" ? data.message : undefined,
        false,
        "Pending plan change reverted"
      );
    } catch (error) {
      console.error("revertPendingPlan failed:", error);
      handleError(error?.message || "Unexpected Error Occurs");
    } finally {
      setPlanActionLoading(false);
    }
  };

  const reactivateSubscriptionRequest = async () => {
    if (!subscriptionId) return;
    try {
      setTerminating(true);
      const res = await subscriptionSerivces.reactivateSubscription({
        subscription_id: subscriptionId,
      });
      const data = unwrapData(res);
      await fetchBillingState();
      handleSuccess(
        typeof data?.message === "string" ? data.message : undefined,
        false,
        "Subscription renewal restored"
      );
    } catch (error) {
      console.error("reactivateSubscription failed:", error);
      handleError(error?.message || "Unexpected Error Occurs");
    } finally {
      setTerminating(false);
    }
  };

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      <BaseDialog
        open={Boolean(paymentSuccess)}
        closeDisabled
        title='Payment Successful'
        subtitle='Redirecting to home'
        icon={<CheckCircleOutlineIcon color='success' />}
        dialogSx={{ zIndex: PAYMENT_SUCCESS_DIALOG_Z_INDEX }}
        backdropSx={{ zIndex: PAYMENT_SUCCESS_DIALOG_Z_INDEX - 1 }}
        paperSx={{ zIndex: PAYMENT_SUCCESS_DIALOG_Z_INDEX }}
        iconColor='success.main'
      >
        <Stack spacing={2}>
          <Typography variant='body1' sx={{ fontWeight: 500 }}>
            {paymentSuccess?.message ?? "Payment completed successfully."}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            You will be redirected to the home page in {redirectCountdown}{" "}
            second{redirectCountdown === 1 ? "" : "s"}…
          </Typography>
        </Stack>
      </BaseDialog>

      {loading ? (
        <ComponentLoading />
      ) : (
        <>
          {(activating || terminating || planActionLoading) && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1200,
                bgcolor: "rgba(255,255,255,0.6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <Container maxWidth='lg' sx={{ py: 2 }}>
            {showPlansTab ? (
              <Stack spacing={2}>
                <Button
                  variant='text'
                  onClick={() => setShowPlansTab(false)}
                  sx={{ alignSelf: "flex-start", textTransform: "none" }}
                >
                  ← Back to billing
                </Button>
                <PlansTab
                  isGuestForTrial={isGuestForTrial}
                  apiPlans={catalogPlans}
                  plansLoading={catalogPlansLoading}
                  ctaBusy={planActionLoading}
                  onChangePlan={handleChangePlan}
                  onPlanIdMissing={() =>
                    handleError(
                      "Could not match this plan to your catalog. Try again or contact support."
                    )
                  }
                  currentPlanId={global_authState?.subscription?.plan_id}
                  pendingPlanId={billingState.subscriptionStat?.pending_plan_id}
                />
              </Stack>
            ) : (
              <TenantBillingOverview
                billingData={billingState.billingData}
                subscriptionStat={billingState.subscriptionStat}
                onViewPlans={() => setShowPlansTab(true)}
                onActivateSubscription={handleActivate}
                onConfirmPayment={handlePayment}
                onTerminateSubscription={handleTerminate}
                revertPendingPlanRequest={revertPendingPlanRequest}
                reactivateSubscriptionRequest={reactivateSubscriptionRequest}
                activating={activating}
                charging={charging}
                terminating={terminating}
                paymentHistory={paymentHistory}
                paymentHistoryLoading={paymentHistoryLoading}
                paymentHistoryLoaded={paymentHistoryLoaded}
                onLoadPaymentHistory={handleLoadPaymentHistory}
              />
            )}
          </Container>
        </>
      )}
    </Box>
  );
};

export default BillingPanel;
